import React, { useEffect, useState } from 'react';
import { App, Button, Input, InputNumber, Modal, Segmented, Space, Typography } from 'antd';
import { GiftOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Order, OrderItem } from '../../../types/orders';
import { useApplyDiscountMutation } from '../../../services/ordersApi';
import { useAppSelector } from '../../../hooks/redux';
import { getErrorMessage } from '../../../utils/errorHelpers';
import { piecesAmount } from '../../../utils/discounts';

const { Text } = Typography;

type Mode = 'courtesy' | 'percentage' | 'amount';

const COURTESY_REASON = 'Cortesía de la casa';
const REASONS = [COURTESY_REASON, 'Queja del cliente', 'Error del personal', 'Cliente frecuente', 'Promoción'];
const OTHER = 'Otro';
const QUICK_PERCENTAGES = [10, 15, 20, 25, 50];

const money = (n: number) => `$${n.toFixed(2)}`;

interface ItemDiscountModalProps {
  visible: boolean;
  order?: Order;
  item: OrderItem | null;
  onCancel: () => void;
  /** Después de aplicar: el padre refresca orden y descuentos. */
  onApplied?: () => void;
}

/**
 * Cortesía o descuento a UN producto de la cuenta.
 *
 * Pensado para no teclear: la cortesía completa son tres toques desde el ⋯ del
 * renglón (Cortesía o descuento → Aplicar), con el motivo y quien autoriza ya
 * precargados. El monto y el tope se miden contra el importe del renglón; el
 * backend los vuelve a validar.
 */
const ItemDiscountModal: React.FC<ItemDiscountModalProps> = ({ visible, order, item, onCancel, onApplied }) => {
  const { message } = App.useApp();
  const { user } = useAppSelector(state => state.auth);
  const [applyDiscount, { isLoading }] = useApplyDiscountMutation();

  const [mode, setMode] = useState<Mode>('courtesy');
  const [pieces, setPieces] = useState(1);
  const [percentage, setPercentage] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState<string>(COURTESY_REASON);
  const [otherReason, setOtherReason] = useState('');
  const [authorizedBy, setAuthorizedBy] = useState('');

  // El modal vive montado: sin reset al abrir, el renglón anterior dejaría sus
  // valores precargados en el siguiente (ver DiscountModal).
  useEffect(() => {
    if (!visible || !item) return;
    setMode('courtesy');
    setPieces(item.quantity);
    setPercentage(null);
    setAmount(null);
    setReason(COURTESY_REASON);
    setOtherReason('');
    setAuthorizedBy([user?.firstName, user?.lastName].filter(Boolean).join(' '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, item?.id]);

  if (!item || !order) return null;

  const itemName = item.dailyMenuName || item.productName;
  const hasSeveral = item.quantity > 1;

  const discountAmount =
    mode === 'courtesy'
      ? piecesAmount(item, pieces)
      : mode === 'percentage'
        ? Math.round(item.total * ((percentage ?? 0) / 100) * 100) / 100
        : amount ?? 0;
  const remaining = Math.max(0, item.total - discountAmount);

  const finalReason = reason === OTHER ? otherReason.trim() : reason;
  const valueOk =
    mode === 'courtesy'
      ? pieces >= 1 && pieces <= item.quantity
      : mode === 'percentage'
        ? (percentage ?? 0) > 0 && (percentage ?? 0) <= 100
        : (amount ?? 0) > 0 && (amount ?? 0) <= item.total;
  const canApply = valueOk && !!finalReason && !!authorizedBy.trim();

  const changeMode = (next: Mode) => {
    setMode(next);
    // Cambiar a %/$ con "Cortesía de la casa" marcado sería un motivo que no
    // describe lo que se aplica; se deja elegir. De vuelta a cortesía, se precarga.
    if (next === 'courtesy' && !reason) setReason(COURTESY_REASON);
    if (next !== 'courtesy' && reason === COURTESY_REASON) setReason('');
  };

  const handleApply = async () => {
    if (!canApply) return;

    // La cortesía SIEMPRE es el monto fijo de las piezas regaladas, también cuando
    // son todas. Como 100% crecía con el renglón: si al editar la orden el café
    // pasaba de 1 a 3, los 3 salían gratis sin que nadie lo autorizara.
    const payload =
      mode === 'courtesy'
        ? { type: 'amount' as const, value: discountAmount }
        : mode === 'percentage'
          ? { type: 'percentage' as const, value: percentage ?? 0 }
          : { type: 'amount' as const, value: amount ?? 0 };

    try {
      await applyDiscount({
        orderId: order.id,
        orderItemId: item.id,
        ...payload,
        reason: finalReason,
        authorizedBy: authorizedBy.trim(),
      }).unwrap();
      message.success(mode === 'courtesy' ? `Cortesía aplicada: ${itemName}` : `Descuento aplicado: ${itemName}`);
      onApplied?.();
    } catch (error) {
      message.error(getErrorMessage(error, 'No se pudo aplicar el descuento'));
    }
  };

  const pill = (active: boolean): React.CSSProperties => ({
    borderRadius: 999,
    ...(active
      ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)', background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)' }
      : {}),
  });

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width={440}
      destroyOnClose
      title={
        <Space size={8}>
          <GiftOutlined />
          <span>Cortesía o descuento</span>
        </Space>
      }
      footer={
        <Space>
          <Button onClick={onCancel} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="primary" onClick={handleApply} loading={isLoading} disabled={!canApply}>
            {mode === 'courtesy' ? 'Aplicar cortesía' : 'Aplicar descuento'}
          </Button>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <Text strong style={{ fontSize: 15, minWidth: 0 }}>
            {item.quantity}x {itemName}
          </Text>
          <Text style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(item.total)}</Text>
        </div>

        <Segmented<Mode>
          block
          value={mode}
          onChange={changeMode}
          options={[
            { label: 'Cortesía', value: 'courtesy' },
            { label: '%', value: 'percentage' },
            { label: '$', value: 'amount' },
          ]}
        />

        {mode === 'courtesy' && hasSeveral && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <Text type="secondary">Piezas de cortesía</Text>
            <Space size={10}>
              <Button
                shape="circle"
                icon={<MinusOutlined />}
                aria-label="Una pieza menos"
                disabled={pieces <= 1}
                onClick={() => setPieces(p => Math.max(1, p - 1))}
              />
              <Text strong style={{ minWidth: 48, textAlign: 'center', display: 'inline-block', fontVariantNumeric: 'tabular-nums' }}>
                {pieces} de {item.quantity}
              </Text>
              <Button
                shape="circle"
                icon={<PlusOutlined />}
                aria-label="Una pieza más"
                disabled={pieces >= item.quantity}
                onClick={() => setPieces(p => Math.min(item.quantity, p + 1))}
              />
            </Space>
          </div>
        )}

        {mode === 'percentage' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Space wrap size={[8, 8]}>
              {QUICK_PERCENTAGES.map(p => (
                <Button key={p} style={pill(percentage === p)} onClick={() => setPercentage(p)}>
                  {p}%
                </Button>
              ))}
            </Space>
            <InputNumber
              value={percentage}
              onChange={v => setPercentage(v)}
              min={1}
              max={100}
              precision={0}
              addonAfter="%"
              placeholder="Otro porcentaje"
              style={{ width: '100%' }}
            />
          </div>
        )}

        {mode === 'amount' && (
          <InputNumber
            value={amount}
            onChange={v => setAmount(v)}
            min={0.01}
            max={item.total}
            precision={2}
            addonBefore="$"
            placeholder={`Hasta ${money(item.total)}`}
            style={{ width: '100%' }}
            autoFocus
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Text type="secondary">Motivo</Text>
          <Space wrap size={[8, 8]}>
            {[...REASONS, OTHER].map(r => (
              <Button key={r} size="small" style={pill(reason === r)} onClick={() => setReason(r)}>
                {r}
              </Button>
            ))}
          </Space>
          {reason === OTHER && (
            <Input
              value={otherReason}
              onChange={e => setOtherReason(e.target.value)}
              placeholder="Escribe el motivo"
              maxLength={200}
              showCount
              autoFocus
            />
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Text type="secondary">Autoriza</Text>
          <Input
            value={authorizedBy}
            onChange={e => setAuthorizedBy(e.target.value)}
            placeholder="Nombre de quien autoriza"
            maxLength={100}
            showCount
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            paddingTop: 12,
            borderTop: '1px solid var(--border-color)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <Text type="secondary">
            {discountAmount > 0 ? `-${money(discountAmount)}` : 'Sin descuento'}
          </Text>
          <Text strong style={{ fontSize: 16 }}>
            Queda {money(remaining)}
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default ItemDiscountModal;
